import React, { useState, useRef, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Settings, Users, ChevronDown, LogOut, User, Bell, BarChart2, Search, Clock, LayoutDashboard, Activity } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import axios from 'axios';
import { BrandingSettings } from './types';
import EmailTemplates from './pages/EmailTemplates';
import Build from './pages/Build';
import Admin from './pages/Admin';
import Contacts from './pages/Contacts';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Profile from './pages/Profile';
import AlumniImport from './pages/AlumniImport';
import Alerts from './pages/Alerts';
import Sends from './pages/Sends';
import DomainLookup from './pages/DomainLookup';
import DomainReport from './pages/DomainReport';
import SendStats from './pages/SendStats';
import SendPreview from './pages/SendPreview';
import Queue from './pages/Queue';
import MetricsLogs from './pages/MetricsLogs';
import Dashboard from './pages/Dashboard';
import GuestView from './pages/GuestView';
import ProtectedRoute from './components/ProtectedRoute';
import DarkModeToggle from './components/DarkModeToggle';
import { APP_VERSION } from './version';
import { GUEST_TOKEN_KEY } from './context/AuthContext';
import { RoleFeatures } from './types';

/** Allows guest token holders (via ?gt= URL param or sessionStorage) to view
 *  standalone report pages without logging in. Falls back to ProtectedRoute
 *  for regular authenticated users. */
function GuestAwareRoute({ children, scope, featureKey }: {
  children: React.ReactNode;
  scope: string;
  featureKey: keyof RoleFeatures;
}) {
  const { search, pathname } = useLocation();

  // Synchronously check for guest token — either from ?gt= URL param or sessionStorage
  const isGuest = React.useMemo(() => {
    const params = new URLSearchParams(search);
    const gt = params.get('gt') ?? sessionStorage.getItem(GUEST_TOKEN_KEY);
    if (!gt) return false;
    try {
      const parts = gt.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (payload.role !== 'guest') return false;
      if (!payload.scope?.includes(scope)) return false;
      if (payload.exp * 1000 <= Date.now()) return false;
      // Persist to sessionStorage and strip from URL bar
      sessionStorage.setItem(GUEST_TOKEN_KEY, gt);
      return true;
    } catch { return false; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Strip ?gt= from URL bar after storing (one-time on mount)
  React.useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.has('gt')) {
      window.history.replaceState({}, '', pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isGuest) return <>{children}</>;
  return <ProtectedRoute featureKey={featureKey}>{children}</ProtectedRoute>;
}

function NavUserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', onOutside);
    return () => document.removeEventListener('click', onOutside);
  }, []);

  if (!user) {
    return (
      <Link
        to="/login"
        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
      >
        Sign in
      </Link>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 rounded-md hover:bg-gray-100"
      >
        <span className="max-w-[120px] truncate">{user.email}</span>
        <ChevronDown className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-48 rounded-md bg-white shadow-lg border border-gray-200 py-1 z-10">
          <Link
            to="/profile"
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => setOpen(false)}
          >
            <User className="h-4 w-4" />
            Profile
          </Link>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => {
              logout();
              setOpen(false);
              navigate('/login');
            }}
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
    isActive
      ? 'border-blue-500 text-blue-600'
      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
  }`;

/** Site notice when Interspire is off or unreachable (data from public GET /api/health). */
function InterspireHealthBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get<{
          integration?: { interspire?: { enabled?: boolean; reachable?: boolean } };
        }>('/api/health');
        if (cancelled) return;
        const int = data?.integration?.interspire;
        if (!int) {
          setVisible(false);
          return;
        }
        setVisible(!int.enabled || !int.reachable);
      } catch {
        if (!cancelled) setVisible(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      className="bg-amber-50 border-b border-amber-200 text-amber-950 px-4 py-2.5 text-sm text-center"
      role="status"
    >
      Interspire is disabled or not configured. To configure your Interspire integration,{' '}
      <Link to="/admin?tab=interspire" className="font-medium text-amber-900 underline hover:text-amber-950">
        click here
      </Link>
      .
    </div>
  );
}

function AppLayout() {
  const { user, canAccess } = useAuth();
  const [branding, setBranding] = useState<BrandingSettings>({ logoDataUrl: '', footerText: '' });

  const fetchBranding = useCallback(async () => {
    try {
      const { data } = await axios.get<BrandingSettings>('/api/settings/branding');
      setBranding(data);
    } catch {
      // non-critical — default branding stays
    }
  }, []);

  useEffect(() => { fetchBranding(); }, [fetchBranding]);

  // Expose refresh so Admin page can notify after save
  (window as unknown as Record<string, unknown>).__refreshBranding = fetchBranding;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-[1480px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            {/* Left — logo */}
            <div className="flex-1 flex items-center">
              <div className="flex-shrink-0 flex items-center">
                {branding.logoDataUrl ? (
                  <img src={branding.logoDataUrl} alt="Logo" className="h-8 w-auto max-w-[180px] object-contain" />
                ) : (
                  <div className="h-8 w-8 rounded-md bg-blue-600 flex items-center justify-center">
                    <span className="text-white font-bold text-lg leading-none">Z</span>
                  </div>
                )}
                <span className="ml-2 text-xl font-bold text-gray-900">EmailCraft</span>
              </div>
            </div>

            {/* Center — nav links */}
            <div className="hidden sm:flex sm:items-center sm:space-x-8">
              {canAccess('dashboard') && (
                <NavLink to="/dashboard" className={navLinkClass}>
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  Dashboard
                </NavLink>
              )}
              {canAccess('buildCampaign') && (
                <NavLink to="/build" className={navLinkClass}>
                  <Mail className="h-4 w-4 mr-2" />
                  Build Campaign
                </NavLink>
              )}
              {canAccess('contacts') && (
                <NavLink to="/contacts" className={navLinkClass}>
                  <Users className="h-4 w-4 mr-2" />
                  Contacts
                </NavLink>
              )}
              {canAccess('alerts') && (
                <NavLink to="/alerts" className={navLinkClass}>
                  <Bell className="h-4 w-4 mr-2" />
                  Alerts
                </NavLink>
              )}
              {canAccess('emailSends') && (
                <NavLink to="/sends" className={navLinkClass}>
                  <BarChart2 className="h-4 w-4 mr-2" />
                  Sends
                </NavLink>
              )}
              {canAccess('emailQueue') && (
                <NavLink to="/queue" className={navLinkClass}>
                  <Clock className="h-4 w-4 mr-2" />
                  Queue
                </NavLink>
              )}
              {canAccess('metricsLogs') && (
                <NavLink to="/metrics" className={navLinkClass}>
                  <Activity className="h-4 w-4 mr-2" />
                  Metrics
                </NavLink>
              )}
              {canAccess('domainLookup') && (
                <NavLink to="/domain-lookup" className={navLinkClass}>
                  <Search className="h-4 w-4 mr-2" />
                  Domain Lookup
                </NavLink>
              )}
            </div>

            {/* Right — admin gear + user menu */}
            <div className="flex-1 flex items-center justify-end gap-2">
              {user?.role === 'admin' && (
                <NavLink to="/admin" title="Admin Settings" className={({ isActive }) =>
                  `p-2 rounded-md ${isActive ? 'text-blue-600 bg-blue-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`
                }>
                  <Settings className="h-5 w-5" />
                </NavLink>
              )}
              <NavUserMenu />
            </div>
          </div>
        </div>
      </nav>

      <InterspireHealthBanner />

      <main className="w-[90%] mx-auto py-6 px-6 flex-1 min-h-[840px]">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/" element={<Navigate to="/contacts" replace />} />
          <Route
            path="/build"
            element={
              <ProtectedRoute featureKey="buildCampaign">
                <Build />
              </ProtectedRoute>
            }
          />
          <Route
            path="/build-from-event"
            element={<Navigate to="/build" replace />}
          />
          <Route
            path="/contacts"
            element={
              <ProtectedRoute featureKey="contacts">
                <Contacts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates"
            element={
              <ProtectedRoute featureKey="emailTemplates">
                <EmailTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/alumni-import"
            element={
              <ProtectedRoute featureKey="alumniImport">
                <AlumniImport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/alerts"
            element={
              <ProtectedRoute featureKey="alerts">
                <Alerts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sends"
            element={
              <ProtectedRoute featureKey="emailSends">
                <Sends />
              </ProtectedRoute>
            }
          />
          <Route
            path="/queue"
            element={
              <ProtectedRoute featureKey="emailQueue">
                <Queue />
              </ProtectedRoute>
            }
          />
          <Route
            path="/metrics"
            element={
              <ProtectedRoute featureKey="metricsLogs">
                <MetricsLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute featureKey="dashboard">
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/domain-lookup"
            element={
              <ProtectedRoute featureKey="domainLookup">
                <DomainLookup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/domain-lookup/report"
            element={
              <ProtectedRoute featureKey="domainLookup">
                <DomainReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="*"
            element={
              <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
                <h1 className="text-4xl font-bold text-gray-800 mb-2">404</h1>
                <p className="text-gray-500 mb-4">Page not found</p>
                <Link to="/build" className="text-blue-600 hover:underline text-sm">Go to Build Campaign</Link>
              </div>
            }
          />
        </Routes>
      </main>
      <footer className="w-full py-3 px-6">
        <div className="flex justify-center">
          <p className="text-xs text-gray-500 dark:text-slate-400 italic text-center">
            {branding.footerText && <>{branding.footerText}{' '}</>}ZENTO Email Craft |{' '}
            <a href="https://zentosaas.com" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 dark:hover:text-slate-200 underline-offset-2">
              www.zentosaas.com
            </a>
            {' '}| v{APP_VERSION}
          </p>
        </div>
      </footer>

      <DarkModeToggle />
    </div>
  );
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <Routes>
          {/* Standalone report pages — no nav/footer wrapper */}
          <Route
            path="/sends/:statid"
            element={
              <GuestAwareRoute scope="sends" featureKey="emailSends">
                <SendStats />
              </GuestAwareRoute>
            }
          />
          <Route
            path="/sends/:statid/preview"
            element={
              <GuestAwareRoute scope="sends" featureKey="emailSends">
                <SendPreview />
              </GuestAwareRoute>
            }
          />
          <Route
            path="/queue/:jobid/preview"
            element={
              <GuestAwareRoute scope="queue" featureKey="emailQueue">
                <SendPreview />
              </GuestAwareRoute>
            }
          />
          {/* Shareable guest view — no login required, governed by guest JWT */}
          <Route path="/guest" element={<GuestView />} />
          {/* All other routes go through AppLayout (nav + footer) */}
          <Route path="*" element={<AppLayout />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App; 